import React, { useMemo, useCallback, useState, useEffect } from "react";
import {
  Button,
  Table,
  Typography,
  Input,
  Modal,
  Form,
  Popover,
  Tabs,
  Select,
  Divider,
  Space,
  Tooltip,
  Descriptions,
} from "antd";
import type { TableColumnsType } from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CopyOutlined,
  ReloadOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import Layout from "../../Components/Layout";
import { UserCell } from "../../Components/UserAvatar";
import { formatRut } from "../../utils/formatRut";
import { formatPhoneConCodigo } from "../../utils/formatPhone";
import TelefonoInput from "../../Components/TelefonoInput";
import { reglaTelefono } from "../../utils/telefonoE164";
import { passwordMinRule } from "../../utils/passwordPolicy";
import PasswordModal from "../../Components/PasswordModal";
import AdminPasswordTools from "../../Components/AdminPasswordTools";
import { useNavigate } from "react-router-dom";
import PageHeader from "../../Components/PageHeader";
import { useClientesLogic, PropertyWithOwnership } from "./useClientesLogic";
import { configOptionApi } from "../../services/configOption.api";
import { formatPropertyName } from "../../utils/propertyDisplay";
import "./clientes.css";

const { Title } = Typography;

/**
 * Muestra "—" cuando el valor está vacío/nulo, para que una celda en blanco
 * se lea como "sin dato" intencional y no como un bug de carga.
 */
const dash = (v?: string | null) => {
  const s = v == null ? "" : String(v).trim();
  return s ? s : <span style={{ color: "#bbb" }}>—</span>;
};

/**
 * Factory de sorter alfabético tolerante a undefined y sensible a locale
 * (ordena bien acentos y ñ). Se usa en las columnas de identidad.
 */
const byText = (key: string) => (a: any, b: any) =>
  String(a?.[key] ?? "").localeCompare(String(b?.[key] ?? ""), "es", {
    sensitivity: "base",
  });

/**
 * Contenido de la fila expandible: los "datos de pago (quién recibe)" que
 * antes saturaban la tabla con 7 columnas. Las claves son las mismas para
 * cliente y propietario, así que un único render sirve para ambas pestañas.
 * Ver decisión de UX en el historial de chat (2026-06-23): fila expandible.
 */
const renderPagoExpandido = (record: any) => (
  <Descriptions
    size="small"
    bordered
    column={{ xs: 1, sm: 2, lg: 3 }}
    title="Datos de pago (quién recibe)"
  >
    <Descriptions.Item label="RUT receptor">
      {dash(record.rutQuienRecibe)}
    </Descriptions.Item>
    <Descriptions.Item label="Banco">{dash(record.bancoQuienRecibe)}</Descriptions.Item>
    <Descriptions.Item label="Tipo de cuenta">
      {dash(record.tipocuentaQuienRecibe)}
    </Descriptions.Item>
    <Descriptions.Item label="N° de cuenta">
      {dash(record.numerocuentaQuienRecibe)}
    </Descriptions.Item>
    <Descriptions.Item label="Nombre receptor">
      {dash(record.nombreQuienRecibe)}
    </Descriptions.Item>
    <Descriptions.Item label="Email receptor">
      {dash(record.emailQuienRecibe)}
    </Descriptions.Item>
    <Descriptions.Item label="Tel receptor">
      {dash(formatPhoneConCodigo(record.telefonoQuienRecibe))}
    </Descriptions.Item>
  </Descriptions>
);

/**
 * Nombre accesible de un botón de acción de una fila: «Editar a Ana Soto». Sin él, un lector de pantalla decía «edit» / «delete»
 * (el nombre del ícono, en inglés) sin decir a quién (ronda F3, H4). Sin nombre en la fila queda solo la acción.
 */
const etiquetaAccion = (accion: string, nombre: unknown): string => {
  const quien = String(nombre ?? "").trim();
  return quien ? `${accion} a ${quien}` : accion;
};

const ClientesPage: React.FC = () => {
  const navigate = useNavigate();
  const [availableBanks, setAvailableBanks] = useState<string[]>([]);
  const [accountTypes, setAccountTypes] = useState<string[]>([]);

  useEffect(() => {
    const fetchOptions = async () => {
      const [bancosRes, cuentasRes] = await Promise.all([
        configOptionApi.list("banco"),
        configOptionApi.list("tipocuenta"),
      ]);
      setAvailableBanks(bancosRes?.data?.map((o) => o.valor) ?? []);
      setAccountTypes(cuentasRes?.data?.map((o) => o.valor) ?? []);
    };
    fetchOptions();
  }, []);
  const {
    activeTab,
    setActiveTab,
    generatedPassword,
    setGeneratedPassword,
    clients,
    owners,
    propertiesWithOwnership,
    filterPropertyOptions,
    propertyFilter,
    setPropertyFilter,
    bancoFilter,
    setBancoFilter,
    tipocuentaFilter,
    setTipocuentaFilter,
    bancoFilterOptions,
    tipocuentaFilterOptions,
    loading,
    submitting,
    modalVisible,
    editingRecord,
    form,
    sidebarOpen,
    toggleSidebar,
    showModal,
    handleCancel,
    handleSubmit,
    deleteCheck,
    handleDeleteRequest,
    handleDeleteConfirm,
    handleDeleteCancel,
    searchInput,
    setSearchInput,
  } = useClientesLogic();

  // Visibilidad del campo de contraseña en el modal. Lo controlamos para
  // poder REVELAR el valor cuando el admin pulsa "Generar" (así ve la
  // contraseña que se va a enviar). Se reinicia al cerrar el modal.
  const [pwdVisible, setPwdVisible] = useState(false);
  useEffect(() => {
    if (!modalVisible) setPwdVisible(false);
  }, [modalVisible]);

  /**
   * Genera una contraseña legible al azar, con el mismo criterio que el
   * backend (`generateSimplePassword`): 6 letras (sin I/O/l/o ambiguas) +
   * 2 dígitos (sin 0/1). Rellena el campo y lo deja visible para que el
   * admin pueda leerlo/copiarlo antes de guardar.
   */
  const handleGenerarPassword = useCallback(() => {
    const letras = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz";
    const digitos = "23456789";
    const pick = (s: string, n: number) =>
      Array.from(
        { length: n },
        () => s[Math.floor(Math.random() * s.length)],
      ).join("");
    const nueva = pick(letras, 6) + pick(digitos, 2);
    form.setFieldsValue({ password: nueva });
    setPwdVisible(true);
  }, [form]);

  /** Modal property options with current assignment info */
  const modalPropertyOptions = useMemo(() => {
    return propertiesWithOwnership
      .filter((p: PropertyWithOwnership) => {
        // Propietarios: una propiedad puede tener múltiples propietarios, mostrar todas
        if (activeTab === "propietario") return true;
        // Clientes: mostrar solo sin asignar o ya asignadas a este usuario
        return p.currentClientId == null || p.currentClientId === editingRecord?.id;
      })
      .map((p: PropertyWithOwnership) => ({
        label: formatPropertyName(p.nombrepropiedad),
        value: p.id,
      }));
  }, [propertiesWithOwnership, activeTab, editingRecord]);

  const handlePropertyChange = useCallback(
    (selectedIds: number[]) => {
      form.setFieldsValue({ property_ids: selectedIds });
    },
    [form],
  );

  // Nombre de propiedad clickeable → abre su configuración en /propiedades
  // (deep-link ?edit=<id>, que la página de propiedades intercepta y abre el
  // modal de edición de esa propiedad). Pedido del owner: poder saltar a la
  // config de la propiedad desde el listado del cliente/propietario.
  const propLink = (p: any) =>
    p?.id != null ? (
      <Typography.Link
        onClick={(e) => {
          // stopPropagation: la fila expande con click (expandRowByClick) — el
          // link debe navegar sin además expandir/colapsar la fila.
          e.stopPropagation();
          navigate(`/propiedades?edit=${p.id}`);
        }}
      >
        {formatPropertyName(p.nombrepropiedad)}
      </Typography.Link>
    ) : (
      <span>{formatPropertyName(p?.nombrepropiedad)}</span>
    );

  const propiedadesColumn = (title: string, field: string) => ({
    title,
    dataIndex: field,
    key: field,
    render: (props: any[]) => {
      if (!props || props.length === 0) {
        return <span style={{ color: "#999" }}>Sin propiedades</span>;
      }

      if (props.length === 1) {
        return propLink(props[0]);
      }

      return (
        <Popover
          title={title}
          content={
            <ul style={{ paddingLeft: 16, margin: 0 }}>
              {props.map((p) => (
                <li key={p.id}>{propLink(p)}</li>
              ))}
            </ul>
          }
        >
          <Button type="link" onClick={(e) => e.stopPropagation()}>
            Ver ({props.length})
          </Button>
        </Popover>
      );
    },
  });

  // Columnas CLIENTE — solo identidad + propiedades + acciones. Los datos de
  // pago viven en la fila expandible (renderPagoExpandido) para no saturar.
  const columnsCliente = useMemo<TableColumnsType<any>>(
    () => [
      {
        title: "Nombre",
        dataIndex: "nombreUsuario",
        key: "nombre_usuario",
        fixed: "left",
        sorter: byText("nombreUsuario"),
        render: (nombre: string, record: any) => (
          <UserCell
            nombre={nombre}
            avatarSeed={record?.avatar}
            avatarUrl={record?.avatar_url}
            size={30}
          />
        ),
      },
      {
        title: "Email",
        dataIndex: "emailUsuario",
        key: "email_usuario",
        sorter: byText("emailUsuario"),
        render: dash,
      },
      {
        title: "Teléfono",
        dataIndex: "telefonoUsuario",
        key: "telefono_usuario",
        sorter: byText("telefonoUsuario"),
        // Con el código de país separado (+56 9 1234 5678), igual que Administradores y /aseadores; un dato antiguo sin código se
        // muestra como siempre (ronda F3, H2).
        render: (v: string) => dash(formatPhoneConCodigo(v)),
      },
      propiedadesColumn("Propiedades que maneja", "propiedades_maneja"),
      {
        title: "Acciones",
        key: "acciones",
        fixed: "right",
        width: 110,
        render: (_: any, record: any) => (
          // stopPropagation: que el click en las acciones no expanda la fila.
          <span onClick={(e) => e.stopPropagation()}>
            <Space size={4}>
              <Tooltip title="Editar">
                <Button
                  icon={<EditOutlined />}
                  aria-label={etiquetaAccion("Editar", record?.nombreUsuario)}
                  onClick={() => showModal(record)}
                />
              </Tooltip>
              <Tooltip title="Eliminar">
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  aria-label={etiquetaAccion("Eliminar", record?.nombreUsuario)}
                  onClick={() => handleDeleteRequest(record.id)}
                />
              </Tooltip>
            </Space>
          </span>
        ),
      },
    ],
    [showModal, handleDeleteRequest],
  );

  // Columnas PROPIETARIO — identidad propia (incluye RUT del propietario) +
  // propiedades + acciones. Los datos de pago van en la fila expandible.
  const columnsPropietario = useMemo<TableColumnsType<any>>(
    () => [
      {
        title: "Nombre",
        dataIndex: "nombrePropietario",
        key: "nombre_propietario",
        fixed: "left",
        sorter: byText("nombrePropietario"),
        render: dash,
      },
      {
        title: "RUT",
        dataIndex: "rutPropietario",
        key: "rut_propietario",
        sorter: byText("rutPropietario"),
        render: dash,
      },
      {
        title: "Email",
        dataIndex: "emailPropietario",
        key: "email_propietario",
        sorter: byText("emailPropietario"),
        render: dash,
      },
      {
        title: "Teléfono",
        dataIndex: "telefonoPropietario",
        key: "telefono_propietario",
        sorter: byText("telefonoPropietario"),
        render: (v: string) => dash(formatPhoneConCodigo(v)),
      },
      propiedadesColumn("Propiedades del propietario", "propiedades_tiene"),
      {
        title: "Acciones",
        key: "acciones",
        fixed: "right",
        width: 110,
        render: (_: any, record: any) => (
          // stopPropagation: que el click en las acciones no expanda la fila.
          <span onClick={(e) => e.stopPropagation()}>
            <Space size={4}>
              <Tooltip title="Editar">
                <Button
                  icon={<EditOutlined />}
                  aria-label={etiquetaAccion("Editar", record?.nombrePropietario)}
                  onClick={() => showModal(record)}
                />
              </Tooltip>
              <Tooltip title="Eliminar">
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  aria-label={etiquetaAccion("Eliminar", record?.nombrePropietario)}
                  onClick={() => handleDeleteRequest(record.id)}
                />
              </Tooltip>
            </Space>
          </span>
        ),
      },
    ],
    [showModal, handleDeleteRequest],
  );

  const modalTitle =
    activeTab === "cliente"
      ? editingRecord
        ? "Editar cliente"
        : "Nuevo cliente"
      : editingRecord
        ? "Editar propietario"
        : "Nuevo propietario";
  return (
    <Layout
      sidebarOpen={sidebarOpen}
      toggleSidebar={toggleSidebar}
      containerClass="home-container"
      contentClass="home-content"
    >
      <section className="content-section">
        <PageHeader
          title="Gestión de usuarios"
          helpText="Gestión de clientes y propietarios. Crea usuarios y asigna roles."
        />

        <div className="usuarios-toolbar">
          <Input
            placeholder="Buscar por nombre, correo, banco, cuenta..."
            allowClear
            size="large"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            style={{ maxWidth: 400, flex: 1 }}
          />
          <Select
            mode="multiple"
            allowClear
            placeholder="Filtrar por propiedad"
            size="large"
            value={propertyFilter}
            onChange={setPropertyFilter}
            options={filterPropertyOptions}
            optionFilterProp="label"
            style={{ minWidth: 250, flex: 1, maxWidth: 500 }}
          />
          <Select
            mode="multiple"
            allowClear
            placeholder="Filtrar por banco"
            size="large"
            value={bancoFilter}
            onChange={setBancoFilter}
            options={bancoFilterOptions}
            optionFilterProp="label"
            style={{ minWidth: 180, flex: 1, maxWidth: 320 }}
          />
          <Select
            mode="multiple"
            allowClear
            placeholder="Filtrar por tipo de cuenta"
            size="large"
            value={tipocuentaFilter}
            onChange={setTipocuentaFilter}
            options={tipocuentaFilterOptions}
            optionFilterProp="label"
            style={{ minWidth: 180, flex: 1, maxWidth: 320 }}
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            size="large"
            onClick={() => showModal()}
          >
            {activeTab === "cliente" ? "Nuevo cliente" : "Nuevo propietario"}
          </Button>
        </div>

        <Tabs
          activeKey={activeTab}
          onChange={(k) => setActiveTab(k as any)}
          items={[
            {
              key: "cliente",
              label: "Clientes",
              children: (
                <Table
                  columns={columnsCliente}
                  dataSource={clients}
                  rowKey="id"
                  loading={loading}
                  size="middle"
                  scroll={{ x: "max-content" }}
                  // expandRowByClick: la fila completa expande/colapsa con un
                  // click (pedido del owner 2026-07-02 — el "+" solo era muy
                  // incómodo). Los elementos interactivos de la fila (links de
                  // propiedades, popover "Ver", botones de acciones) hacen
                  // stopPropagation para no expandir de rebote.
                  expandable={{
                    expandedRowRender: renderPagoExpandido,
                    expandRowByClick: true,
                  }}
                  rowClassName={() => "fila-expandible"}
                  pagination={{
                    showSizeChanger: true,
                    defaultPageSize: 10,
                    showTotal: (total) =>
                      `${total} cliente${total === 1 ? "" : "s"}`,
                  }}
                />
              ),
            },
            {
              key: "propietario",
              label: "Propietarios",
              children: (
                <Table
                  columns={columnsPropietario}
                  dataSource={owners}
                  rowKey="id"
                  loading={loading}
                  size="middle"
                  scroll={{ x: "max-content" }}
                  expandable={{
                    expandedRowRender: renderPagoExpandido,
                    expandRowByClick: true,
                  }}
                  rowClassName={() => "fila-expandible"}
                  pagination={{
                    showSizeChanger: true,
                    defaultPageSize: 10,
                    showTotal: (total) =>
                      `${total} propietario${total === 1 ? "" : "s"}`,
                  }}
                />
              ),
            },
          ]}
        />

        <Modal
          title={modalTitle}
          open={modalVisible}
          onOk={handleSubmit}
          onCancel={handleCancel}
          okText={editingRecord ? "Actualizar" : "Crear"}
          width={800}
          // Mientras se guarda: spinner + botón "Crear/Actualizar"
          // deshabilitado (confirmLoading), botón cancelar bloqueado y
          // modal no cerrable por click-fuera / Esc / X. Así el admin ve
          // que el proceso está en curso y no puede disparar un doble envío.
          confirmLoading={submitting}
          cancelButtonProps={{ disabled: submitting }}
          maskClosable={!submitting}
          closable={!submitting}
          keyboard={!submitting}
        >
          <Form form={form} layout="vertical">
            <div className="flex-container">
              {/* CLIENTE fields (ignorar propietario) */}
              {activeTab === "cliente" && (
                <>
                  <Form.Item
                    name="nombreUsuario"
                    label="Nombre"
                    rules={[{ required: true }]}
                  >
                    <Input />
                  </Form.Item>
                  <Form.Item
                    name="emailUsuario"
                    label="Email"
                    rules={[{ type: "email" }]}
                  >
                    <Input placeholder="correo@ejemplo.com" />
                  </Form.Item>
                  {/* Teléfonos con código de país. El valor puede quedar vacío; si se escribe, tiene que estar completo. Un dato antiguo
                      sin código que no se toca no impide guardar el resto. */}
                  <Form.Item
                    name="telefonoUsuario"
                    label="Teléfono"
                    rules={[reglaTelefono({ valorInicial: editingRecord?.telefonoUsuario })]}
                  >
                    <TelefonoInput ariaLabel="Teléfono del cliente" />
                  </Form.Item>
                </>
              )}

              {/* PROPIETARIO fields (ignorar usuario) */}
              {activeTab === "propietario" && (
                <>
                  <Form.Item
                    name="nombrePropietario"
                    label="Nombre"
                    rules={[{ required: true }]}
                  >
                    <Input />
                  </Form.Item>
                  <Form.Item name="rutPropietario" label="RUT">
                    <Input
                      placeholder="11.111.111-1"
                      maxLength={12}
                      onChange={(e) =>
                        form.setFieldsValue({
                          rutPropietario: formatRut(e.target.value),
                        })
                      }
                    />
                  </Form.Item>
                  <Form.Item
                    name="emailPropietario"
                    label="Email"
                    rules={[{ type: "email" }]}
                  >
                    <Input placeholder="correo@ejemplo.com" />
                  </Form.Item>
                  <Form.Item
                    name="telefonoPropietario"
                    label="Teléfono"
                    rules={[reglaTelefono({ valorInicial: editingRecord?.telefonoPropietario })]}
                  >
                    <TelefonoInput ariaLabel="Teléfono del propietario" />
                  </Form.Item>
                </>
              )}

              {/* Campos comunes */}
              <Form.Item
                className="full-width"
                name="property_ids"
                label={
                  activeTab === "cliente"
                    ? "Propiedades que maneja"
                    : "Propiedades del propietario"
                }
              >
                <Select
                  mode="multiple"
                  showSearch
                  allowClear
                  options={modalPropertyOptions}
                  optionFilterProp="label"
                  placeholder="Busca y selecciona propiedades..."
                  onChange={handlePropertyChange}
                />
              </Form.Item>

              {/*
                Política unificada de contraseñas (Opción B — ver
                docs/auth-roles.md §"Creación de cuentas").

                CREACIÓN cliente:
                  - Si dejas el campo vacío → el sistema autogenera
                    una contraseña temporal y la envía al correo del
                    cliente. Verás la generada en un modal al final
                    para poder copiarla si quieres.
                  - Si escribes una contraseña → será la contraseña
                    inicial del cliente y ALSO se enviará por correo
                    (para que el cliente tenga el dato a la mano).

                EDICIÓN:
                  - Vacío → no toca la contraseña actual.
                  - Con valor → reemplaza la contraseña actual. NO
                    se envía correo de bienvenida (no es creación).

                Solo aplica a clientes (los propietarios no tienen
                cuenta de login en el sistema).
              */}
              {activeTab === "cliente" && (
                <Form.Item
                  className="full-width"
                  label={editingRecord ? "Nueva contraseña" : "Contraseña inicial"}
                  extra={
                    editingRecord
                      ? "Dejar vacío para mantener la contraseña actual."
                      : "Opcional. Si la dejas vacía, se generará automáticamente y se enviará por correo. Si la escribes (o usas «Generar»), esa será la contraseña inicial del cliente y también se enviará por correo."
                  }
                >
                  {/*
                    El campo en sí va en un Form.Item interno `noStyle` para
                    que el Form lo registre, mientras el externo aporta label
                    y `extra`. Al lado, el botón «Generar» rellena el campo y
                    lo revela: así la elección "autogenerar vs. escribir a
                    mano" es explícita en vez de un comportamiento oculto.
                  */}
                  <Space.Compact style={{ width: "100%" }}>
                    <Form.Item name="password" noStyle rules={[passwordMinRule]}>
                      <Input.Password
                        autoComplete="new-password"
                        placeholder={
                          editingRecord
                            ? "Vacío = no cambiar"
                            : "Vacío = se autogenera y se envía por correo"
                        }
                        visibilityToggle={{
                          visible: pwdVisible,
                          onVisibleChange: setPwdVisible,
                        }}
                      />
                    </Form.Item>
                    <Tooltip title="Generar una contraseña al azar y mostrarla">
                      <Button
                        icon={<ReloadOutlined />}
                        onClick={handleGenerarPassword}
                      >
                        Generar
                      </Button>
                    </Tooltip>
                  </Space.Compact>
                </Form.Item>
              )}

              {/* En EDICIÓN de un cliente, además de la clave directa, el admin
                  puede forzar el correo de recuperación para que el propio
                  cliente fije su contraseña. El generador ya existe arriba
                  (botón «Generar»), por eso showGenerate={false}. */}
              {activeTab === "cliente" && editingRecord && (
                <div className="full-width">
                  <AdminPasswordTools
                    form={form}
                    showGenerate={false}
                    userId={editingRecord.id}
                    currentEmail={editingRecord.emailUsuario}
                  />
                </div>
              )}

              <div className="full-width" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 8 }}>
                <Title level={5} style={{ margin: 0 }}>
                  Datos de pago (quién recibe)
                </Title>
                <Button
                  size="small"
                  icon={<CopyOutlined />}
                  onClick={() => {
                    const vals = form.getFieldsValue();
                    if (activeTab === "cliente") {
                      form.setFieldsValue({
                        nombreQuienRecibe: vals.nombreUsuario,
                        emailQuienRecibe: vals.emailUsuario,
                        telefonoQuienRecibe: vals.telefonoUsuario,
                      });
                    } else {
                      form.setFieldsValue({
                        rutQuienRecibe: vals.rutPropietario,
                        nombreQuienRecibe: vals.nombrePropietario,
                        emailQuienRecibe: vals.emailPropietario,
                        telefonoQuienRecibe: vals.telefonoPropietario,
                      });
                    }
                  }}
                >
                  Copiar datos
                </Button>
              </div>

              <Form.Item name="rutQuienRecibe" label="RUT quien recibe">
                <Input
                  placeholder="11.111.111-1"
                  maxLength={12}
                  onChange={(e) =>
                    form.setFieldsValue({
                      rutQuienRecibe: formatRut(e.target.value),
                    })
                  }
                />
              </Form.Item>
              <Form.Item name="nombreQuienRecibe" label="Nombre quien recibe">
                <Input />
              </Form.Item>
              <Form.Item
                name="emailQuienRecibe"
                label="Email quien recibe"
                rules={[
                  { type: "email", message: "Introduzca un correo válido." },
                ]}
              >
                <Input placeholder="correo@ejemplo.com" />
              </Form.Item>
              <Form.Item
                name="telefonoQuienRecibe"
                label="Teléfono quien recibe"
                rules={[reglaTelefono({ valorInicial: editingRecord?.telefonoQuienRecibe })]}
              >
                <TelefonoInput ariaLabel="Teléfono de quien recibe el pago" />
              </Form.Item>

              <Form.Item name="bancoQuienRecibe" label="Banco">
                <Select
                  showSearch
                  allowClear
                  options={availableBanks.map((b) => ({ label: b, value: b }))}
                  optionFilterProp="label"
                  dropdownRender={(menu) => (
                    <>
                      {menu}
                      <Divider style={{ margin: "8px 0" }} />
                      <div style={{ display: "flex", gap: 8, padding: "0 8px 8px" }}>
                        <Input
                          placeholder="Nuevo banco..."
                          id="new-banco-clientes"
                          onPressEnter={async (e) => {
                            const input = e.currentTarget;
                            const val = input.value.trim();
                            if (!val) return;
                            await configOptionApi.create({ categoria: "banco", valor: val });
                            setAvailableBanks((prev) => [...prev, val]);
                            form.setFieldValue("bancoQuienRecibe", val);
                            input.value = "";
                          }}
                        />
                        <Button
                          type="primary"
                          icon={<PlusOutlined />}
                          onClick={async () => {
                            const input = document.getElementById("new-banco-clientes") as HTMLInputElement;
                            const val = input?.value.trim();
                            if (!val) return;
                            await configOptionApi.create({ categoria: "banco", valor: val });
                            setAvailableBanks((prev) => [...prev, val]);
                            form.setFieldValue("bancoQuienRecibe", val);
                            if (input) input.value = "";
                          }}
                        >
                          Crear
                        </Button>
                      </div>
                    </>
                  )}
                />
              </Form.Item>
              <Form.Item name="tipocuentaQuienRecibe" label="Tipo de cuenta">
                <Select
                  showSearch
                  allowClear
                  options={accountTypes.map((t) => ({ label: t, value: t }))}
                  optionFilterProp="label"
                  dropdownRender={(menu) => (
                    <>
                      {menu}
                      <Divider style={{ margin: "8px 0" }} />
                      <div style={{ display: "flex", gap: 8, padding: "0 8px 8px" }}>
                        <Input
                          placeholder="Nuevo tipo..."
                          id="new-tipocuenta-clientes"
                          onPressEnter={async (e) => {
                            const input = e.currentTarget;
                            const val = input.value.trim();
                            if (!val) return;
                            await configOptionApi.create({ categoria: "tipocuenta", valor: val });
                            setAccountTypes((prev) => [...prev, val]);
                            form.setFieldValue("tipocuentaQuienRecibe", val);
                            input.value = "";
                          }}
                        />
                        <Button
                          type="primary"
                          icon={<PlusOutlined />}
                          onClick={async () => {
                            const input = document.getElementById("new-tipocuenta-clientes") as HTMLInputElement;
                            const val = input?.value.trim();
                            if (!val) return;
                            await configOptionApi.create({ categoria: "tipocuenta", valor: val });
                            setAccountTypes((prev) => [...prev, val]);
                            form.setFieldValue("tipocuentaQuienRecibe", val);
                            if (input) input.value = "";
                          }}
                        >
                          Crear
                        </Button>
                      </div>
                    </>
                  )}
                />
              </Form.Item>
              {/* Atajo de descubrimiento: desde acá solo se pueden CREAR
                  bancos / tipos de cuenta al vuelo. Para renombrarlos o
                  borrarlos está la pestaña "Opciones generales" de
                  Configuración. Abrimos en una pestaña nueva (target=_blank)
                  para no perder el formulario de cliente a medio llenar. */}
              <div style={{ marginTop: -8, marginBottom: 8 }}>
                <Typography.Link
                  href="/configuracion?tab=configuracion"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ fontSize: 12 }}
                >
                  <SettingOutlined style={{ marginRight: 4 }} />
                  Administrar bancos y tipos de cuenta
                </Typography.Link>
              </div>
              <Form.Item
                name="numerocuentaQuienRecibe"
                label="Número de cuenta"
              >
                <Input />
              </Form.Item>
            </div>
          </Form>
        </Modal>
      </section>
      <PasswordModal
        open={!!generatedPassword}
        password={generatedPassword}
        title="Contraseña generada para el cliente"
        onClose={() => setGeneratedPassword(null)}
      />
      <Modal
        open={!!deleteCheck}
        title="Confirmar eliminación"
        okText="Eliminar"
        okButtonProps={{ danger: true }}
        cancelText="Cancelar"
        onOk={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      >
        {deleteCheck && (
          <>
            <p>
              ¿Eliminar a <strong>{deleteCheck.nombre}</strong>?
            </p>
            {deleteCheck.propiedades.length > 0 && (
              <>
                <p style={{ color: "#faad14", fontWeight: 500 }}>
                  Este usuario tiene {deleteCheck.propiedades.length}{" "}
                  propiedad(es) asociada(s). Al eliminarlo se desasociará
                  automáticamente de:
                </p>
                <ul>
                  {deleteCheck.propiedades.map((p) => (
                    <li key={`${p.id}-${p.rol}`}>
                      {formatPropertyName(p.nombre)} <em>(como {p.rol})</em>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </>
        )}
      </Modal>
    </Layout>
  );
};

export default ClientesPage;
